import { useMemo, useState } from 'react';
import MapleMarketDetailView from '@/components/maple-market/MapleMarketDetailView';
import MapleMarketInfoView, { type MapleMarketInfoPageId } from '@/components/maple-market/MapleMarketInfoView';
import MapleMarketLibraryView from '@/components/maple-market/MapleMarketLibraryView';
import MapleMarketStoreView from '@/components/maple-market/MapleMarketStoreView';
import MapleMarketAdminWindow from '@/components/maple-market/admin/MapleMarketAdminWindow';
import type { MapleMarketCategory } from '@/data/mapleMarketApps';
import { useMarketplaceMemberships } from '@/hooks/supabase/useMarketplaceMemberships';

export default function MapleMarketWindow() {
  const [activeCategory, setActiveCategory] = useState<MapleMarketCategory | 'All'>('All');
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const [selectedLibraryAppId, setSelectedLibraryAppId] = useState<string | null>(null);
  const [activePage, setActivePage] = useState<MapleMarketInfoPageId | null>(null);
  const [mode, setMode] = useState<'store' | 'library' | 'admin'>('store');
  const { isSiteAdmin, isVendor } = useMarketplaceMemberships();

  const isAdminAvailable = useMemo(() => isSiteAdmin || isVendor, [isSiteAdmin, isVendor]);

  const handleCategoryChange = (category: MapleMarketCategory | 'All') => {
    setMode('store');
    setSelectedAppId(null);
    setSelectedLibraryAppId(null);
    setActivePage(null);
    setActiveCategory(category);
  };

  const handleSelectApp = (appId: string) => {
    setMode('store');
    setActivePage(null);
    setSelectedLibraryAppId(null);
    setSelectedAppId(appId);
  };

  const handleOpenLibrary = (appId?: string) => {
    setMode('library');
    setActivePage(null);
    setSelectedAppId(null);
    setSelectedLibraryAppId(appId ?? null);
  };

  const handleOpenPage = (page: MapleMarketInfoPageId) => {
    setSelectedAppId(null);
    setSelectedLibraryAppId(null);
    setMode('store');
    setActivePage(page);
  };

  const handleBackToStore = () => {
    setMode('store');
    setActivePage(null);
    setSelectedLibraryAppId(null);
  };

  return (
    <div className="maple-market-scroll-container h-full overflow-auto bg-background">
      {mode === 'admin' && isAdminAvailable ? (
        <MapleMarketAdminWindow onExit={() => setMode('store')} />
      ) : activePage ? (
        <MapleMarketInfoView
          embedded
          page={activePage}
          isAdminAvailable={isAdminAvailable}
          onOpenAdmin={() => setMode('admin')}
          onOpenLibrary={() => handleOpenLibrary()}
          onBackToStore={handleBackToStore}
          onOpenPage={handleOpenPage}
          onSelectCategory={handleCategoryChange}
          onSelectApp={handleSelectApp}
        />
      ) : mode === 'library' ? (
        <MapleMarketLibraryView
          embedded
          appId={selectedLibraryAppId ?? undefined}
          isAdminAvailable={isAdminAvailable}
          onOpenAdmin={() => setMode('admin')}
          onOpenLibrary={handleOpenLibrary}
          onOpenStoreApp={handleSelectApp}
          onBackToStore={handleBackToStore}
          onOpenPage={handleOpenPage}
          onSelectCategory={handleCategoryChange}
        />
      ) : selectedAppId ? (
        <MapleMarketDetailView
          embedded
          appId={selectedAppId}
          isAdminAvailable={isAdminAvailable}
          onOpenAdmin={() => setMode('admin')}
          onOpenLibrary={() => handleOpenLibrary()}
          onOpenPage={handleOpenPage}
          onSelectCategory={handleCategoryChange}
          onSelectApp={handleSelectApp}
        />
      ) : (
        <MapleMarketStoreView
          embedded
          isAdminAvailable={isAdminAvailable}
          onOpenAdmin={() => setMode('admin')}
          onOpenLibrary={() => handleOpenLibrary()}
          onOpenPage={handleOpenPage}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
          onSelectApp={handleSelectApp}
        />
      )}
    </div>
  );
}
